import { useEffect, useRef, useState } from 'react';
import { activeOrganizationId, graphql } from '../api/client';
import { useAuth } from '../auth/AuthProvider';

type Target = { kind: string; candidate: string; field?: string };
type Correction = {
  id: string; jobId: string; target: Target; previousValue: string; correctedValue: string; reason: string;
  evidence: { artifactId: string; fileName: string; quote: string; page?: number };
  status: string; reviewReason: string;
};
type Page = { items: Correction[]; nextCursor: string | null };
const targetIdentity = (target: Target) => JSON.stringify({ ...target,
  kind: target.kind === 'MISSING_OWNER' ? 'OWNER' : target.kind === 'MISSING_NOC' ? 'NOC_INSTRUMENT' : target.kind });
const query = `query Corrections($orderId:ID!,$after:ID){researchCorrections(orderId:$orderId,after:$after){
  items{id jobId target previousValue correctedValue reason evidence status reviewReason} nextCursor}}`;

export function ResearchCorrections({ orderId, jobId, editable, targets, artifacts, onDownload, onChange }: {
  orderId: string; jobId: string; editable: boolean;
  targets: Array<{ label: string; target: Target; value: string }>;
  artifacts: Array<{ id: string; fileName: string }>;
  onDownload: (id: string) => Promise<void>; onChange: () => void;
}) {
  const { token } = useAuth();
  const [page, setPage] = useState<Page>({ items: [], nextCursor: null });
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [targetIndex, setTargetIndex] = useState(() => targets.length ? '0' : 'MISSING_OWNER');
  const [correctedValue, setCorrectedValue] = useState('');
  const [reason, setReason] = useState('');
  const [artifactId, setArtifactId] = useState('');
  const [quote, setQuote] = useState('');
  const [sourcePage, setSourcePage] = useState('');
  const [missingCandidate, setMissingCandidate] = useState('');
  const pendingRequest = useRef<{ fingerprint: string; key: string } | null>(null);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    graphql<{ researchCorrections: Page }>({ query, variables: { orderId }, token, organizationId: activeOrganizationId() })
      .then(data => { if (!cancelled) { setPage(data.researchCorrections); setError(''); } })
      .catch(error => { if (!cancelled) setError((error as Error).message); });
    return () => { cancelled = true; };
  }, [token, orderId, jobId, revision]);
  async function mutate(name: 'proposeResearchCorrection' | 'reviewResearchCorrection', input: Record<string, unknown>) {
    if (!token) return;
    setBusy(true); setError('');
    const fingerprint = JSON.stringify({ name, input });
    if (pendingRequest.current?.fingerprint !== fingerprint) pendingRequest.current = { fingerprint, key: crypto.randomUUID() };
    try {
      await graphql({ query: `mutation Correction($input:JSON!){${name}(input:$input)}`, variables: { input: { ...input, requestKey: pendingRequest.current.key } }, token, organizationId: activeOrganizationId() });
      pendingRequest.current = null;
      setRevision(v => v + 1); onChange();
      if (name === 'proposeResearchCorrection') { setCorrectedValue(''); setReason(''); setQuote(''); }
    } catch (error) { setError((error as Error).message); }
    finally { setBusy(false); }
  }
  async function more() {
    if (!token || !page.nextCursor) return;
    setBusy(true);
    try {
      const data = await graphql<{ researchCorrections: Page }>({ query, variables: { orderId, after: page.nextCursor }, token, organizationId: activeOrganizationId() });
      setPage({ items: [...page.items, ...data.researchCorrections.items], nextCursor: data.researchCorrections.nextCursor });
    } catch (error) { setError((error as Error).message); }
    finally { setBusy(false); }
  }
  const selected = targets[Number(targetIndex)];
  const missing = targetIndex === 'MISSING_OWNER' || targetIndex === 'MISSING_NOC';
  return <section aria-label="Research corrections">
    <h3>Corrections</h3>
    <p>Corrections preserve the original findings and need source review. Reviewed corrections are retained for later research. They do not change the project record.</p>
    {error && <p role="alert" className="alert error">{error}</p>}
    {editable && <details>
      <summary>Correct a finding</summary>
      <form onSubmit={event => { event.preventDefault(); if (selected || missing) void mutate('proposeResearchCorrection', {
        jobId, target: missing ? { kind: targetIndex, candidate: missingCandidate } : selected!.target,
        correctedValue: targetIndex === 'MISSING_NOC' ? missingCandidate : correctedValue, reason,
        evidence: { artifactId, quote, ...(sourcePage ? { page: Number(sourcePage) } : {}) },
      }); }}>
        <label>Finding<select value={targetIndex} onChange={event => setTargetIndex(event.target.value)}>
          {targets.map((target, index) => <option key={index} value={index}>{target.label}</option>)}
          <option value="MISSING_OWNER">Missing parcel owner</option>
          <option value="MISSING_NOC">Missing notice of commencement</option>
        </select></label>
        <p>Original value: {missing ? 'Candidate was not returned' : selected?.value}</p>
        {missing && <label>{targetIndex === 'MISSING_OWNER' ? 'Missing parcel number' : 'Missing notice instrument number'}
          <input required maxLength={2000} value={missingCandidate} onChange={event => setMissingCandidate(event.target.value)} /></label>}
        {targetIndex !== 'MISSING_NOC' && <label>{targetIndex === 'MISSING_OWNER' ? 'Proposed owner name' : 'Corrected value'}<input required maxLength={2000} value={correctedValue} onChange={event => setCorrectedValue(event.target.value)} /></label>}
        <label>Reason<textarea required minLength={10} maxLength={4000} value={reason} onChange={event => setReason(event.target.value)} /></label>
        <label>Supporting evidence<select required value={artifactId} onChange={event => setArtifactId(event.target.value)}>
          <option value="">Select a captured source</option>
          {artifacts.map(artifact => <option key={artifact.id} value={artifact.id}>{artifact.fileName}</option>)}
        </select></label>
        <label>Source quote<textarea required minLength={5} maxLength={2000} value={quote} onChange={event => setQuote(event.target.value)} /></label>
        <label>Page number (required for a PDF)<input type="number" min={1} step={1} value={sourcePage} onChange={event => setSourcePage(event.target.value)} /></label>
        <button className="button" disabled={busy || (!selected && !missing)}>Record correction for review</button>
        {!selected && !missing && <p className="form-hint">Choose what to correct above, or mark it as missing.</p>}
      </form>
    </details>}
    {page.items.map(correction => <CorrectionReview key={`${correction.id}:${correction.status}`} correction={correction}
      conflicts={page.items.filter(other => other.id !== correction.id && other.status === 'REVIEWED' && targetIdentity(other.target) === targetIdentity(correction.target))}
      editable={editable} busy={busy} onDownload={onDownload}
      onReview={(decision, reason, supersedes) => mutate('reviewResearchCorrection', {
        correctionId: correction.id, decision, reason, supersedes,
      })} />)}
    {page.nextCursor && <button className="button button-quiet" disabled={busy} onClick={() => void more()}>Load earlier corrections</button>}
  </section>;
}

function CorrectionReview({ correction, conflicts, editable, busy, onDownload, onReview }: {
  correction: Correction; conflicts: Correction[]; editable: boolean; busy: boolean;
  onDownload: (id: string) => Promise<void>;
  onReview: (decision: string, reason: string, supersedes: string[]) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [supersedes, setSupersedes] = useState<string[]>([]);
  return <article className="inline-card">
    <h4>{correction.target.kind.replaceAll('_', ' ')} · {correction.target.candidate} · {correction.status.toLowerCase()}</h4>
    <p>Original: {correction.previousValue || 'Candidate was not returned'}<br />Correction: {correction.correctedValue}</p>
    <p>{correction.reason}</p>
    <blockquote>{correction.evidence.quote}</blockquote>
    <button className="button button-quiet" onClick={() => void onDownload(correction.evidence.artifactId)}>
      Open {correction.evidence.fileName}{correction.evidence.page ? ` · page ${correction.evidence.page}` : ''}
    </button>
    {correction.status !== 'PROPOSED' && <p>Review: {correction.reviewReason}</p>}
    {editable && ['PROPOSED', 'REVIEWED'].includes(correction.status) && <div>
      <label>Source review or withdrawal reason<textarea minLength={10} maxLength={4000} value={reason} onChange={event => setReason(event.target.value)} /></label>
      {correction.status === 'PROPOSED' && conflicts.map(conflict => <label key={conflict.id}>
        <input type="checkbox" checked={supersedes.includes(conflict.id)} onChange={event => setSupersedes(event.target.checked ? [...supersedes, conflict.id] : supersedes.filter(id => id !== conflict.id))} />
        Supersede reviewed value: {conflict.correctedValue}
      </label>)}
      {correction.status === 'PROPOSED' && <button className="button" disabled={busy || reason.trim().length < 10 || conflicts.some(c => !supersedes.includes(c.id))}
        onClick={() => void onReview('REVIEWED', reason, supersedes)}>Confirm correction against source</button>}
      <button className="button button-quiet" disabled={busy || reason.trim().length < 10}
        onClick={() => void onReview('REJECTED', reason, [])}>{correction.status === 'REVIEWED' ? 'Withdraw correction' : 'Reject correction'}</button>
    </div>}
  </article>;
}
